import React from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as NavCtaArrow } from '../../home-redesign/assets/nav-cta-arrow.svg';
import { BUSINESS_PORTAL_URL, PARTNER_REGISTER_URL } from '../constants';

type Variant = 'primary' | 'secondary' | 'dark';

interface PartnersCtaButtonProps {
  children: React.ReactNode;
  href?: string;
  variant?: Variant;
  className?: string;
  fullWidth?: boolean;
  showArrow?: boolean;
  arrowClassName?: string;
  external?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-chisme-primary text-white hover:bg-chisme-primary/90',
  secondary:
    'border border-[#2a0f2a] bg-transparent text-[#2a0f2a] hover:bg-[#2a0f2a] hover:text-white',
  dark: 'bg-[#2a0f2a] text-white hover:bg-[#3d1a3b]',
};

const PartnersCtaButton: React.FC<PartnersCtaButtonProps> = ({
  children,
  href = PARTNER_REGISTER_URL,
  variant = 'primary',
  className = '',
  fullWidth = false,
  showArrow = true,
  arrowClassName = '',
  external = href.startsWith('http') || href.startsWith('mailto:'),
}) => {
  const classNames = `inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-6 py-3 font-jakarta text-sm font-bold tracking-[-0.01em] transition-all duration-200 sm:px-8 sm:text-[15px] ${
    fullWidth ? 'w-full' : ''
  } ${variantClasses[variant]} ${className}`;

  // Our own business portal opens in the same tab; other sites in a new one.
  const newTab = href.startsWith('http') && !href.startsWith(BUSINESS_PORTAL_URL);

  const content = (
    <>
      {children}
      {showArrow && variant === 'primary' && (
        <span className={`inline-flex shrink-0 ${arrowClassName}`}>
          <NavCtaArrow aria-hidden className="size-[13px] max-w-none shrink-0" />
        </span>
      )}
    </>
  );

  // In-page anchor (e.g. "Cómo funciona"): a router <Link> only changes the
  // hash and doesn't scroll, so scroll to the section ourselves.
  if (href.startsWith('#')) {
    const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>) => {
      const target = document.getElementById(href.slice(1));
      if (!target) return;
      event.preventDefault();
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      window.history.replaceState(window.history.state, '', href);
    };
    return (
      <a href={href} onClick={scrollToSection} className={classNames}>
        {content}
      </a>
    );
  }

  if (!external) {
    return (
      <Link to={href} className={classNames}>
        {content}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={classNames}
    >
      {content}
    </a>
  );
};

export default PartnersCtaButton;
